/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The shared unsaved-changes channel between a section and the save bar.
 *
 * A section owns its draft; it does not own the bar. It publishes two facts
 * — whether it has unsaved changes, and whether a save is in flight — plus
 * the two callbacks that act on them. The page reads the same facts to decide
 * whether leaving the tab needs a confirmation.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';

/** What the save bar and the page can see about the active section's draft. */
interface SettingsDirtyState {
  isDirty: boolean;
  isSaving: boolean;
  save: () => void;
  discard: () => void;
}

/**
 * No-operation placeholder.
 */
const NOOP = () => { /* no section has registered a draft */ };

const SettingsDirtyContext = createContext<SettingsDirtyState>({
  isDirty: false, isSaving: false, save: NOOP, discard: NOOP,
});

interface DirtyFlags {
  isDirty: boolean;
  isSaving: boolean;
}

interface DirtyHandlers {
  onSave: () => void;
  onDiscard: () => void;
}

/** The setter side of the channel, kept separate so sections cannot read it. */
interface DirtyRegistry {
  publishFlags: (flags: DirtyFlags) => void;
  publishHandlers: (handlers: DirtyHandlers) => void;
}

const DirtyRegistryContext = createContext<DirtyRegistry | null>(null);

/**
 * Provides the unsaved-changes channel to one settings surface.
 *
 * @param props - Component props.
 * @param props.children - The settings surface.
 * @returns The provider element.
 */
export function SettingsDirtyProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<DirtyFlags>({ isDirty: false, isSaving: false });
  // Handlers live in a ref rather than in state: they are new identities on
  // every render of the section that owns them, and storing them in state
  // would turn each render into another render.
  const handlers = useRef<DirtyHandlers>({ onSave: NOOP, onDiscard: NOOP });

  const publishFlags = useCallback((next: DirtyFlags) => {
    setFlags((prev) => (
      prev.isDirty === next.isDirty && prev.isSaving === next.isSaving ? prev : next
    ));
  }, []);

  const publishHandlers = useCallback((next: DirtyHandlers) => {
    handlers.current = next;
  }, []);

  const registry = useMemo(() => ({ publishFlags, publishHandlers }), [publishFlags, publishHandlers]);

  const state = useMemo<SettingsDirtyState>(() => ({
    isDirty: flags.isDirty,
    isSaving: flags.isSaving,
    save: () => handlers.current.onSave(),
    discard: () => handlers.current.onDiscard(),
  }), [flags]);

  return (
    <DirtyRegistryContext.Provider value={registry}>
      <SettingsDirtyContext.Provider value={state}>
        {children}
      </SettingsDirtyContext.Provider>
    </DirtyRegistryContext.Provider>
  );
}

/**
 * Reads the active section's unsaved-changes state.
 *
 * @returns The dirty state and its two actions.
 */
export function useSettingsDirty(): SettingsDirtyState {
  return useContext(SettingsDirtyContext);
}

/**
 * Publishes a section's draft state to the save bar.
 *
 * Call it from the section that owns the draft. On unmount — which is what
 * changing tabs does — the registration is cleared, so the bar cannot keep
 * offering to save a draft that is no longer on screen.
 *
 * @param registration - The section's draft state and actions.
 */
export function useRegisterSettingsDirty(registration: {
  isDirty: boolean;
  isSaving?: boolean;
  onSave: () => void;
  onDiscard: () => void;
}) {
  const registry = useContext(DirtyRegistryContext);
  const { isDirty, isSaving = false, onSave, onDiscard } = registration;

  useEffect(() => {
    registry?.publishHandlers({ onSave, onDiscard });
  }, [registry, onSave, onDiscard]);

  useEffect(() => {
    registry?.publishFlags({ isDirty, isSaving });
  }, [registry, isDirty, isSaving]);

  useEffect(() => () => {
    registry?.publishFlags({ isDirty: false, isSaving: false });
    registry?.publishHandlers({ onSave: NOOP, onDiscard: NOOP });
  }, [registry]);
}
